// What the signed-in user may do in the organigram, as pure predicates over the `effective` list from
// GET /me/permissions. Import-free so the spec loads it directly, with no TestBed and no
// CustomerdatabaseService - the Karma harness cannot run a spec that pulls that service in.
//
// Two separate questions, deliberately not merged:
//   1. may this user be here at all      -> organigramAccessDenied(), which replaces the whole page
//   2. may this user do this one thing   -> can(), which hides one button
// Everything here decides what to RENDER. It is NOT enforcement: the organigram REST backend
// authorizes on the X-Account-Id / X-User-Id headers this client sends itself, and the customer-project
// receivers authorize on ids in the request body, so a hidden button is the only thing standing
// between a user and most of these writes today. Treat that as a known gap to close server-side, not
// as something these predicates can fix.

export type OrganigramPermission =
  // Live run state on the canvas is part of view; the Audit Logs RECORD is not - see .admin below.
  | 'organigram.view'      // see the organigram, its live runs, the agents' knowledge
  | 'organigram.operate'   // chat, approve a waiting step, cancel a run, download run files, start a pipeline
  | 'organigram.edit'      // change the org structure and the agents' knowledge
  // Also the Audit Logs record: whoever builds a workflow needs to see what it did when it ran.
  | 'organigram.build'     // edit the workflow, publish triggers, define pipelines, webhook secrets, signing keys
  // The organigram's own settings, plus what reports on account SPEND: create/rename/archive an
  // organization, the Access screen, Dashboards and Insights (Usage & Credits per Agent). His call
  // 2026-09-21. Audit Logs was here too for one revision and then moved down to .build - a builder
  // needs the run record, but not the bill.
  | 'organigram.admin';

// Server-reported grant state. `no_role` is deliberately NOT in the deny list below: a user who has a
// grant but no role in any slot simply comes back with an empty `effective`, and every gate here
// already answers that correctly. Only `suspended` and `no_grant` mean "you do not belong here".
export type PermissionsStatus = 'ok' | 'no_role' | 'suspended' | 'no_grant';

// The four role slots, exactly as the API sends them. `jobs` and `agents` have no roles yet and come
// back null. All four are modelled because GET returns all four - a PUT only sends the ones it is
// changing (partial update since 2026-09-21), so nothing here has to be echoed back.
export interface PermissionsRoles {
  account: string | null;
  organigram: string | null;
  jobs: string | null;
  agents: string | null;
}

// How the permission fetch ended, and the reason it is three states rather than a boolean.
//
// Only a REAL refusal from the backend (401/403) takes anything away. A timeout, a 5xx, or a call that
// simply has not come back yet leaves the UI exactly as it behaved before this API existed. "We could
// not ask" must never render as "you may not": one bad gateway would otherwise turn every open canvas
// read-only, and that is indistinguishable from a bug to the person looking at it.
export type PermissionsLoad =
  | { state: 'unknown' }
  | { state: 'refused' }
  | { state: 'loaded'; effective: string[]; status: PermissionsStatus; roles: PermissionsRoles };

export const UNKNOWN_PERMISSIONS: PermissionsLoad = { state: 'unknown' };

// May this user do this one thing? Unknown means yes - see PermissionsLoad for why.
export function can(load: PermissionsLoad, permission: OrganigramPermission): boolean {
  if (load.state === 'unknown') { return true; }
  if (load.state === 'refused') { return false; }
  if (load.status === 'suspended' || load.status === 'no_grant') { return false; }
  return load.effective.indexOf(permission) !== -1;
}

// Should the page be replaced by a "no access, contact your administrator" screen? Answers from the
// same three inputs as can(), but it is a different decision: this one hides the organigram itself,
// so a user who is merely role-less (status 'no_role', empty effective) is refused here too - there is
// nothing for them to look at.
export function organigramAccessDenied(load: PermissionsLoad): boolean {
  if (load.state === 'unknown') { return false; }
  if (load.state === 'refused') { return true; }
  if (load.status === 'suspended' || load.status === 'no_grant') { return true; }
  return load.effective.indexOf('organigram.view') === -1;
}

// Role catalogues for the members-screen dropdowns. The tenant's role sheet is the authority and
// Christian maintains it; this is a copy for rendering only - ask him before adding a name.
//
// These names are for the new grants API and nothing else. They must never be written into
// authService.userRole, the teammembers `status` field or the users `role` field: 'admin', 'user' and
// 'owner' already mean something different in those three, and teammembers.status === 'admin' is what
// decides whether a recruiter sees every location or only their own.
export const ACCOUNT_ROLES = ['member', 'admin', 'owner'];
export const ORGANIGRAM_ROLES = ['user', 'org_editor', 'org_builder', 'org_admin', 'owner'];

const ALL_PERMISSIONS: OrganigramPermission[] = [
  'organigram.view', 'organigram.operate', 'organigram.edit', 'organigram.build', 'organigram.admin'
];

// The backend's role ladder, for previewing a role only. /me/permissions stays the authority.
export const ORGANIGRAM_ROLE_EFFECTIVE: { [role: string]: OrganigramPermission[] } = {
  user: ALL_PERMISSIONS.slice(0, 1),
  org_editor: ALL_PERMISSIONS.slice(0, 3),
  org_builder: ALL_PERMISSIONS.slice(0, 4),
  org_admin: ALL_PERMISSIONS.slice(),
  owner: ALL_PERMISSIONS.slice(),
};

// What `actual` would look like with `role`. Intersects with what the user really holds, so a
// preview can only take permissions away, never add one.
export function previewPermissions(actual: PermissionsLoad, role: string | null): PermissionsLoad {
  const wanted = role ? ORGANIGRAM_ROLE_EFFECTIVE[role] : undefined;
  if (!wanted) { return actual; }
  const held: string[] = actual.state === 'unknown'
    ? wanted
    : actual.state === 'refused' || actual.status === 'suspended' || actual.status === 'no_grant'
      ? []
      : actual.effective;
  const roles = actual.state === 'loaded' ? actual.roles : { account: null, organigram: null, jobs: null, agents: null };
  return {
    state: 'loaded',
    effective: wanted.filter(p => held.indexOf(p) !== -1),
    status: 'ok',
    roles: { ...roles, organigram: role }
  };
}

// Every account member sees the organigram by default: no grant, or a grant without an organigram
// role, renders as Viewer. Only a suspension or a real refusal (401/403) keeps someone out.
export function withDefaultViewer(load: PermissionsLoad): PermissionsLoad {
  if (load.state !== 'loaded' || load.status === 'suspended') { return load; }
  if (load.status === 'no_grant') { return { ...load, effective: ['organigram.view'], status: 'ok' }; }
  if (load.effective.indexOf('organigram.view') !== -1) { return load; }
  return { ...load, effective: ['organigram.view', ...load.effective], status: 'ok' };
}

// Customer logins with full rights in their own tenant. They keep the fail-open below while their
// project has no access API to answer, but they are not MONA staff and see no MONA internals.
const FULL_ACCESS_CUSTOMER_EMAILS = ['bluearrow@mona-ai.co.uk'];

export function isFullAccessCustomerEmail(email: unknown): boolean {
  return typeof email === 'string' && FULL_ACCESS_CUSTOMER_EMAILS.indexOf(email.trim().toLowerCase()) !== -1;
}

// A customer must never see more than their real role, so for them an unanswered or failed
// permission check renders as view-only. MONA staff keep the fail-open described at PermissionsLoad.
export function customerSafePermissions(load: PermissionsLoad, isMonaStaff: boolean): PermissionsLoad {
  if (load.state !== 'unknown' || isMonaStaff === true) { return load; }
  return {
    state: 'loaded',
    effective: ['organigram.view'],
    status: 'ok',
    roles: { account: null, organigram: null, jobs: null, agents: null }
  };
}

// Insights (account spend) and Audit Logs are MONA-internal: staff only, and hidden while a staff
// member previews a customer role.
export function canSeeMonaInternals(isMonaStaff: boolean, previewRole: string | null): boolean {
  return isMonaStaff === true && !previewRole;
}
